import Button, { LoadingButton } from '@atlaskit/button';
import HipchatChevronDoubleDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-double-down';
import HipchatChevronDoubleUpIcon from '@atlaskit/icon/glyph/hipchat/chevron-double-up';
import RefreshIcon from '@atlaskit/icon/glyph/refresh';
import Tooltip from '@atlaskit/tooltip';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, DraggableProvided, DraggableRubric, DraggableStateSnapshot, Droppable, DroppableProvided, DroppableStateSnapshot, DropResult } from 'react-beautiful-dnd';
import { areEqual, FixedSizeList as List } from 'react-window';
import styled from '@emotion/styled';
import { ActionWrapper, ActionWrapperInnerLeft, ActionWrapperInnerRight } from '../../common/ActionWrapper';
import { GlobalContext } from '../../common/GlobalContext';
import { AuthenticatedUserCapabilities, CustomFieldType, IdAndValue } from '../../../model/model';
import { DefaultOptionView } from '../default-option/DefaultOptionView';
import { FieldEditorCreateNewOptionRow } from './FieldEditorCreateNewOptionRow';
import { CustomScrollbarsForVirtualList } from './FieldEditorCustomScrollbars';
import { FieldEditorSort, FieldEditorSortInDatabase } from './FieldEditorInSortInDatabase';
import { FieldEditorOptionListHeader } from './FieldEditorOptionListHeader';
import { FieldEditorViewAndEditOptionRow } from './FieldEditorViewAndEditOptionRow';
import { OptionValidationErrorForView, FieldEditorActionReturnType } from '../../stateful/field-editor-stateful/FieldEditorStateful';
import { FieldEditorSearchBar } from './FieldEditorSearchBar';
import { OptionSearchData, OptionSearchDataChange, filterOptionsBySearchKey, OptionSearchMatchMarker } from './search/custom-field-options-search-logic';
import { fieldEditor_showEditChildOptionButton, getLastOptionIdOrNull } from '../../util/ui-helper';
import { DTOCustomFieldOption, DTOGetDefaultOption, DTOSetDefaultOption } from '../../../generated-common';
import { CCLoadingPlaceholder } from 'components/ui/CCLoadingPlaceholder';
import { token } from '@atlaskit/tokens';

export interface OptionListColumnDimensions {
  positionPx: number;
  positionPaddingLeftPx: number;
  activePx: number;
  valuePx: number | null;
  defaultPx: number;
  childPx: number;
  deletePx: number;
}

export interface FieldEditorOptionAction {}
export class FieldEditorOptionActionUpdate implements FieldEditorOptionAction {
  constructor(public option: DTOCustomFieldOption) {}
}
export class FieldEditorOptionActionCreate implements FieldEditorOptionAction {
  constructor(public option: DTOCustomFieldOption) {}
}
export class FieldEditorOptionActionSetDefault implements FieldEditorOptionAction {
  constructor(public defaultOption: DTOSetDefaultOption) {}
}
export class FieldEditorOptionActionUnSetDefault implements FieldEditorOptionAction {}
export class FieldEditorOptionActionDelete implements FieldEditorOptionAction {
  constructor(public option: DTOCustomFieldOption) {}
}
export class FieldEditorOptionActionOpenChildren implements FieldEditorOptionAction {
  constructor(public option: DTOCustomFieldOption) {}
}
export class FieldEditorOptionActionReOrderOption implements FieldEditorOptionAction {
  constructor(public option: DTOCustomFieldOption, public sourceIndex: number, public destinationIndex: number) {}
}
export class FieldEditorOptionActionSortAlphabetically implements FieldEditorOptionAction {
  constructor(public sort: FieldEditorSort) {}
}
export class FieldEditorOptionActionRefresh implements FieldEditorOptionAction {}

export const AlignRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
export const FieldEditor = ({
  customFieldId,
  contextId,
  options,
  defaultOption, // has only optionIds"
  defaultOptionEnriched, // has values too!
  fieldType,
  isLoading,
  isSorting,
  isDeleting,
  isUpdatingOptionIdOrNull,
  parentOptionIds,
  onUpdateOption,
  validationErrorFromServer,
  authenticatedUserCapabilities,
  allOptionIdsAndValuesLowerCase,
}: {
  customFieldId: string;
  contextId: number;
  options: DTOCustomFieldOption[];
  defaultOption: DTOGetDefaultOption;
  defaultOptionEnriched: DTOGetDefaultOption;
  fieldType: CustomFieldType;
  isLoading: boolean;
  isSorting: boolean;
  isDeleting: string | null;
  isUpdatingOptionIdOrNull: string | null;
  parentOptionIds: null | string[];
  onUpdateOption: (action: FieldEditorOptionAction) => Promise<FieldEditorActionReturnType>;
  validationErrorFromServer: OptionValidationErrorForView | null;
  authenticatedUserCapabilities: AuthenticatedUserCapabilities;
  allOptionIdsAndValuesLowerCase: IdAndValue[];
}) => {
  const { t } = useContext(GlobalContext);
  //
  // SCROLL STUFF
  //
  const listRef = useRef<List>(null);
  const scrollToSync = (scrollToIndex: number) => {
    if (scrollToIndex !== -1 && listRef && listRef.current) {
      listRef!.current!.scrollToItem(scrollToIndex);
    }
  };
  const scrollToBottom = () => scrollToSync(options.length);
  const scrollToTop = () => scrollToSync(0);
  //
  // SEARCH STUFF
  //
  const [searchData, setSearchData] = useState<OptionSearchData>({
    searchKey: '',
    currentOfMaxFound: 0,
    currentOptionId: '0',
    currentOptionIndex: 0,
    maxFound: 0,
    hasNext: false,
    hasPrevious: false,
  });
  const [searchInputChange, setSearchInputChange] = useState<OptionSearchDataChange>({ searchKey: '', direction: 'UNCHANGED' });
  useEffect(() => {
    let isSubscribed = true;
    // https://dev.to/n1ru4l/homebrew-react-hooks-useasynceffect-or-how-to-handle-async-operations-with-useeffect-1fa8
    const runEffectAsync = async () => {
      if (isSubscribed && searchInputChange.searchKey === '') {
        // == clear search!
        setSearchData({
          searchKey: '',
          currentOfMaxFound: 0,
          currentOptionId: '0',
          currentOptionIndex: 0,
          maxFound: 0,
          hasNext: false,
          hasPrevious: false,
        });
      } else {
        if (isSubscribed && searchInputChange.searchKey) {
          // Note even if not subscribed anymore
          // Early set searchKey, so that typed chars are not "schlucked"
          setSearchData({ ...searchData, searchKey: searchInputChange.searchKey });
        }
        if (isSubscribed) {
          const result = filterOptionsBySearchKey(options, searchData, searchInputChange);
          if (isSubscribed) {
            if (searchInputChange.searchKey) {
              // avoid searchInput parallel racing
              setSearchData({ ...searchData, ...result, searchKey: searchInputChange.searchKey });
            } else {
              setSearchData({ ...searchData, ...result });
            }
            // console.log('SCROLLTO INDEX', result.currentOptionIndex);
            if (result.currentOptionIndex !== -1) {
              scrollToSync(result.currentOptionIndex);
            }
          }
        }
      }
    };
    runEffectAsync();
    return () => {
      isSubscribed = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInputChange]);

  //
  // DRAG AND DROP METHODS - https://github.com/atlassian/react-beautiful-dnd
  //
  const onDragStart = () => {};
  const onDragEnd = (result: DropResult) => {
    if (result && result.destination && result.draggableId && result.destination.index !== result.source.index) {
      const optionId = result.draggableId;
      const filteredOptions = options.filter((o) => `${o.id}` === optionId);
      if (filteredOptions && filteredOptions[0]) {
        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;
        onUpdateOption(new FieldEditorOptionActionReOrderOption(filteredOptions[0], sourceIndex, destinationIndex));
      }
    } else {
      console.log('CEP: drag and drop failed');
    }
  };

  // Column sizes
  const optionListColumnDimensions = {
    positionPx: 50,
    positionPaddingLeftPx: 5,
    activePx: 70,
    valuePx: null,
    defaultPx: 70,
    childPx: 70,
    deletePx: 100,
  } as OptionListColumnDimensions;

  // VirtualList single row: https://github.com/atlassian/react-beautiful-dnd/blob/master/stories/src/virtual/react-window/list.jsx
  interface RowProps {
    data: DTOCustomFieldOption[];
    index: number;
    style: Object;
  }
  const FieldEditorViewAndEditOptionRowMemo = React.memo(({ data, index, style }: RowProps) => {
    const o: DTOCustomFieldOption = data[index];
    return (
      <Draggable key={`option-drag-${o.id}`} draggableId={`${o.id}`} index={index} isDragDisabled={isDeleting !== null}>
        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
          <FieldEditorViewAndEditOptionRow
            searchMatch={{ searchKey: searchData.searchKey, isCurrent: o && `${o.id}` === `${searchData.currentOptionId}` } as OptionSearchMatchMarker}
            option={o}
            defaultOption={defaultOption}
            fieldType={fieldType}
            parentOptionIds={parentOptionIds}
            onUpdateOption={onUpdateOption}
            validationErrorFromServer={validationErrorFromServer}
            authenticatedUserCapabilities={authenticatedUserCapabilities}
            allOptionIdsAndValuesLowerCase={allOptionIdsAndValuesLowerCase}
            columnDimensions={optionListColumnDimensions}
            isDeleting={isDeleting}
            isUpdatingOptionIdOrNull={isUpdatingOptionIdOrNull}
            isLoading={isLoading}
            provided={provided}
            isDragging={snapshot.isDragging}
            isGroupedOver={Boolean(snapshot.combineTargetFor)}
            style={{ margin: 0, ...style }}
            index={index}
          />
        )}
      </Draggable>
    );
  }, areEqual);

  const ScrollUp = () => (
    <div style={{ width: '100%', height: 24, position: 'relative' }}>
      <div data-e2e-scroll-to-top="true" style={{ top: 15, right: -11, width: 32, height: 32, position: 'absolute', textAlign: 'center' }}>
        <Tooltip content={t('fieldEditor.scrollToTop')} position="top">
          <Button appearance="subtle" spacing="none" iconBefore={<HipchatChevronDoubleUpIcon label="up" primaryColor={token('color.text.subtlest', '#ccc')} />} onClick={() => scrollToTop()} />
        </Tooltip>
      </div>
    </div>
  );

  const ScrollDown = () => (
    <div style={{ width: '100%', height: 32, position: 'relative' }}>
      <div data-e2e-scroll-to-bottom="true" style={{ top: 0, right: -11, width: 32, height: 28, position: 'absolute', textAlign: 'center' }}>
        <Tooltip content={t('fieldEditor.scrollToBottom')}>
          <Button appearance="subtle" spacing="none" iconBefore={<HipchatChevronDoubleDownIcon label="down" primaryColor={token('color.text.subtlest', '#ccc')} />} onClick={() => scrollToBottom()} />
        </Tooltip>
      </div>
    </div>
  );

  return (
    <div data-e2e-field-editor={parentOptionIds ? `${getLastOptionIdOrNull(parentOptionIds)}` : 'null'} data-testid="ce-fieldeditor-container">
      <ActionWrapper>
        <ActionWrapperInnerLeft>
          <Tooltip content={t('fieldEditor.refresh')} position="top">
            <span data-e2e-field-editor-refresh="true">
              <LoadingButton
                appearance="subtle"
                isLoading={isLoading === true || isSorting === true || isDeleting !== null}
                iconBefore={<RefreshIcon label="refresh" primaryColor={token('color.text.accent.blue.bolder', '#253858')} />}
                onClick={() => onUpdateOption(new FieldEditorOptionActionRefresh())}
              />
            </span>
          </Tooltip>
          <div style={{ marginLeft: '5px', maxWidth: '50px' }}>
            <FieldEditorSortInDatabase isSorting={isSorting} onSort={(sort: FieldEditorSort) => onUpdateOption(new FieldEditorOptionActionSortAlphabetically(sort))} />
          </div>
          <div style={{ marginLeft: '5px', maxWidth: '50px' }}>
            {getLastOptionIdOrNull(parentOptionIds) === null ? (
              <>
                <DefaultOptionView
                  customFieldId={customFieldId}
                  contextId={contextId}
                  defaultOption={defaultOptionEnriched}
                  fieldType={fieldType}
                  allOptions={options}
                  isLoading={isLoading || isDeleting !== null}
                  onUnsetDefaultOption={() => onUpdateOption(new FieldEditorOptionActionUnSetDefault())}
                />
              </>
            ) : null}
          </div>
        </ActionWrapperInnerLeft>
        <ActionWrapperInnerRight>
          <FieldEditorSearchBar searchData={searchData} onSearchInputChanged={(change) => setSearchInputChange(change)} isDisabled={isLoading} />
          {/* parentOptionId === null && <BulkImportModal onUpdateOption={onUpdateOption} isLoading={isLoading} /> */}
        </ActionWrapperInnerRight>
      </ActionWrapper>
      <FieldEditorOptionListHeader
        hasChildOptions={fieldEditor_showEditChildOptionButton(getLastOptionIdOrNull(parentOptionIds), undefined, fieldType)}
        columnDimensions={optionListColumnDimensions}
      />
      <FieldEditorCreateNewOptionRow
        onCreatedOption={(createOption) => onUpdateOption(new FieldEditorOptionActionCreate(createOption))}
        columnDimensions={optionListColumnDimensions}
        hasChildOptions={fieldEditor_showEditChildOptionButton(getLastOptionIdOrNull(parentOptionIds), undefined, fieldType)}
        scrollTopBottom={() => scrollToBottom()}
        rightTopComponent={<>{options.length > 20 && <ScrollUp />}</>}
      />
      {isLoading === true && options.length === 0 && (
        <div data-testid="ce-field-editor-no-options-still-loading" style={{ textAlign: 'center', paddingTop: '20px', paddingLeft: '115px' }}>
          <div style={{ marginBottom: '20px' }}>
            <CCLoadingPlaceholder width={500} height={25} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <CCLoadingPlaceholder width={500} height={25} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <CCLoadingPlaceholder width={500} height={25} />
          </div>
        </div>
      )}
      {isLoading === false && options.length === 0 && (
        <div data-testid="ce-field-editor-no-options" style={{ textAlign: 'center', padding: '20px' }}>
          {t('fieldEditor.emptyOptionsListMessage')}
        </div>
      )}
      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <Droppable
          droppableId={'optonsdroppable'}
          type={'list'}
          ignoreContainerClipping={false}
          isDropDisabled={false}
          isCombineEnabled={false}
          mode="virtual"
          renderClone={(provided: DraggableProvided, snapshot: DraggableStateSnapshot, rubric: DraggableRubric) => (
            <FieldEditorViewAndEditOptionRow
              option={options[rubric.source.index]}
              searchMatch={
                {
                  searchKey: searchData.searchKey,
                  isCurrent: options[rubric.source.index] && `${options[rubric.source.index].id}` === `${searchData.currentOptionId}`,
                } as OptionSearchMatchMarker
              }
              defaultOption={defaultOption}
              fieldType={fieldType}
              parentOptionIds={parentOptionIds}
              onUpdateOption={onUpdateOption}
              validationErrorFromServer={validationErrorFromServer}
              authenticatedUserCapabilities={authenticatedUserCapabilities}
              allOptionIdsAndValuesLowerCase={allOptionIdsAndValuesLowerCase}
              isUpdatingOptionIdOrNull={isUpdatingOptionIdOrNull}
              isDeleting={isDeleting}
              isLoading={isLoading}
              columnDimensions={optionListColumnDimensions}
              provided={provided}
              isDragging={snapshot.isDragging}
              style={{ margin: 0 }}
              index={rubric.source.index}
            />
          )}
        >
          {(droppableProvided: DroppableProvided, dropSnapshot: DroppableStateSnapshot) => (
            <List
              ref={listRef}
              height={getLastOptionIdOrNull(parentOptionIds) === null ? 500 : 300}
              itemCount={options.length}
              outerElementType={CustomScrollbarsForVirtualList} // https://codesandbox.io/s/bvaughnreact-window-react-scrollbars-custom-pjyxs
              itemSize={50}
              width={'100%'}
              // you will want to use List.outerRef rather than List.innerRef as it has the correct height when the list is unpopulated
              outerRef={droppableProvided.innerRef}
              itemData={options}
            >
              {FieldEditorViewAndEditOptionRowMemo}
            </List>
          )}
        </Droppable>
      </DragDropContext>
      {options.length > 20 && <ScrollDown />}
    </div>
  );
};
